import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Checkbox, DropdownProps } from 'semantic-ui-react';
import _ from 'lodash';
import { Link, useParams } from 'react-router-dom';
import { getCounselorsList } from '../../../Services/SpecialistMeetingServices';
import { searchEmployerSpotlightPresentationParticipant, ExportEmployerSpotlightPresentationParticipant } from '../../../Services/EmployerSpotlightPresentationServices';
import { AxiosResponse } from 'axios';
import { utils } from '../../../helpers/utils';
import AppConfig from '../../../Constants/config';
import moment from 'moment';

export interface PageSizeOption {
    key: number;
    value: string | number;
    text: string;
}

export interface GetESPPLPayload {
    sortByField: string | null;
    sortOrderDesc: boolean;
    searchText: string | null;
    recordsPerPage: number;
    pageNum: number;
    counselorId: number;
    EmployerSpotlightPresentationId?: string;
    EmployerSpotlightPresentationParticipantId? : string[];
}

export interface GetESPPLDataRow {
    [key: string]: any;
    counselor: number;
    email: string | undefined;
    employerSpotlightPresentationParticipantId: string | undefined;
    name: string[];
    phone: string | undefined;
}

export interface GetESPPLData {
    participantsCount: number;
    gridData: GetESPPLDataRow[];
    description: string;
    location: string;
    organization: string;
    presentationDateTime: string;
    presentationModeCode: string;
    topic: string;
}

export interface MasterDataItem {
    value: string;
    id: number;
}

export interface MasterData {
    counselorIdOptions: PageSizeOption[],
}

export interface PresentationData {
    description: string;
    location: string;
    organization: string;
    presentationDateTime: string;
    presentationModeCode: string;
    topic: string;
}

export interface MasterDataResponse {

}

export interface ColumnDefinition {
    columnName: string;
    sortable: boolean;
    columnHeader: string;
    decorator?: Function;
}

export function useESPParticipantsList(saveState: boolean = true, statePropName?: string) {

    let { ESPId } = useParams<{ ESPId: string }>();

    const pageSizeOptions = useMemo<PageSizeOption[]>(() => {
        return [
            { key: 3, value: 3, text: '3' },
            { key: 5, value: 5, text: '5' },
            { key: 10, value: 10, text: '10' },
            { key: 15, value: 15, text: '15' },
            { key: 20, value: 20, text: '20' },
            { key: 50, value: 50, text: '50' },
            { key: 100, value: 100, text: '100' }
        ];
    }, []);

    const searchRef = useRef<HTMLInputElement>(null);
    const [masterData, setMasterData] = useState<MasterData>();
    const [recordsPerPage, setRecordsPerPage] = useState<number>(10);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [pageNum, setPageNum] = useState<number>(1);
    const [sortByField, setSortByField] = useState<string | null>(null);
    const [sortOrderDesc, setSortOrderDesc] = useState<boolean>(true);
    const [searchText, setSearchText] = useState<string | null>(null);
    const [data, setData] = useState<GetESPPLDataRow[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedParticipants, setSelectedParticipants] = useState<Map<string, GetESPPLDataRow>>(new Map<string, GetESPPLDataRow>());

    const [presentationData, setPresentationData] = useState<PresentationData>(
        {
            description: '',
            location: '',
            organization: '',
            presentationDateTime: '',
            presentationModeCode: '',
            topic: '',
        },
    );
    const [counselorId, setCounselorId] = useState<number>(0);

    const handlePageSizeChange: ((event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => void) | undefined = (event, data) => {
        setRecordsPerPage(data.value as number);
        const payload: GetESPPLPayload = {
            recordsPerPage: data.value as number,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: pageNum,
            counselorId: counselorId,
        };
        _fetchData(payload);
    }

    const handlePaginationChange: (newPageNumber: number) => void = (newPageNumber) => {
        setPageNum(newPageNumber);
        const payload: GetESPPLPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: newPageNumber,
            counselorId: counselorId,
        };
        _fetchData(payload);
    }

    const handleCounselorIdChange: (newValue: number) => void = (newValue) => {
        setCounselorId(newValue);
        setPageNum(1);
        const payload: GetESPPLPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: 1,
            counselorId: newValue
        };
        _fetchData(payload);
    }

    const handleSortChange: (newSortColumnName: string) => void = (newSortColumnName) => {
        let newIsDesc = sortOrderDesc;
        let newColumnName = sortByField;
        if (newSortColumnName === sortByField) {
            newIsDesc = !newIsDesc;
        } else {
            newColumnName = newSortColumnName;
        }

        setSortOrderDesc(newIsDesc);
        setSortByField(newColumnName);
        setPageNum(1);
        const payload: GetESPPLPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: newColumnName,
            sortOrderDesc: newIsDesc,
            searchText: searchText,
            pageNum: 1,
            counselorId: counselorId,
        };
        _fetchData(payload);
    }

    const handleSearchChangeFn: (event: React.SyntheticEvent<HTMLElement, Event>) => void = (event) => {
        //setSearchText((event.target as HTMLInputElement).value);
        setSearchText((event.target as HTMLInputElement).value);
        setPageNum(1);
        const payload: GetESPPLPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: (event.target as HTMLInputElement).value,
            pageNum: 1,
            counselorId: counselorId,
        };
        _fetchData(payload);
    }
    const handleSearchChange = _.debounce(handleSearchChangeFn, 1000);

    const resetSearch: (event: React.SyntheticEvent<HTMLElement, Event>) => void = (event) => {
        if (searchRef.current !== null) {
            (searchRef.current as HTMLInputElement).value = '';
        }
        setSearchText(null);
        setPageNum(1);
        const payload: GetESPPLPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: null,
            pageNum: 1,
            counselorId: counselorId,
        };
        _fetchData(payload);
    }

    const resetFilter: (event: React.SyntheticEvent<HTMLElement, Event>) => void = (event) => {
        setSearchText(null);
        if (searchRef.current) {
            (searchRef.current as HTMLInputElement).value = '';
        }
        setPageNum(1);
        const payload: GetESPPLPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: null,
            pageNum: 1,
            counselorId: counselorId,
        };
        _fetchData(payload);
    }

    const handleSelectBoxClick: (newValue: boolean, participantId: string) => void = (newValue, participantId)=>{
        if(newValue === true){
            let currParticipant = data.filter((item)=>{
                return item.employerSpotlightPresentationParticipantId == participantId;
            })[0];
            if(currParticipant){
                selectedParticipants.set(participantId, currParticipant);
            }
        }else {
            let currParticipant = data.filter((item)=>{
                return item.employerSpotlightPresentationParticipantId == participantId;
            })[0];
            if(currParticipant){
                selectedParticipants.delete(participantId);
            }
        }
        setIsLoading(false);
    }

    const exportToExcel: () => void = () => {
        const defaultPayload: GetESPPLPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: pageNum,
            counselorId: counselorId,
            EmployerSpotlightPresentationParticipantId: []
        };

        let payload: GetESPPLPayload = defaultPayload;
        selectedParticipants.forEach((item)=>{
            if(payload.EmployerSpotlightPresentationParticipantId){
                payload.EmployerSpotlightPresentationParticipantId.push((item.employerSpotlightPresentationParticipantId || 0) .toString()); 
            }
        });
        //console.log(payload);
        
        payload.EmployerSpotlightPresentationId = ESPId;
        if(payload.EmployerSpotlightPresentationParticipantId && payload.EmployerSpotlightPresentationParticipantId?.length > 0){
            setIsLoading(true);
            ExportEmployerSpotlightPresentationParticipant(payload, false).then((result) => {
                setIsLoading(false);
    
                let response: AxiosResponse<unknown, any> = result as AxiosResponse;
                if (response.status === 200 && response.data) {
                    utils.DownloadBlobExcelFileWithContentDisposition(response, `SpotlightParticipants_${moment().format('MM_dd_yyyy_HH_mm_ss')}.xlsx`);
                }
            }, (error) => {
                setIsLoading(false);
                console.log(error);
            });
        }
        
    }



    const _fetchData: (overwritePayload?: GetESPPLPayload) => void = (overwritePayload) => {
        const defaultPayload: GetESPPLPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: pageNum,
            counselorId: 0,
        };

        let payload: GetESPPLPayload = defaultPayload;
        if (overwritePayload !== undefined) {
            payload = overwritePayload;
        }
        //console.log(payload);
        setIsLoading(true);
        if (saveState) {
            _saveState(payload);
        }
        payload.EmployerSpotlightPresentationId = ESPId;
        searchEmployerSpotlightPresentationParticipant(payload, false).then((result) => {
            setIsLoading(false);

            let response: AxiosResponse<unknown, GetESPPLData> = result as AxiosResponse;
            if (response.status === 200 && response.data) {

                let data = response.data as GetESPPLData;
                setData(data.gridData);
                setTotalRecords(data.gridData.length);
                if (!(presentationData && presentationData.description)) {
                    let newPresentationData: PresentationData = {
                        description: data.description,
                        location: data.location,
                        organization: data.organization,
                        presentationDateTime: data.presentationDateTime,
                        topic: data.topic,
                        presentationModeCode: data.presentationModeCode,
                    };
                    setPresentationData(newPresentationData);
                }
            }
        }, (error) => {
            setIsLoading(false);
            console.log(error);
        });
    }

    const _fetchMasterData: (callback?: Function) => void = (callBack = () => { }) => {
        setIsLoading(true);
        getCounselorsList(false).then((result) => {
            setIsLoading(false);
            let response: AxiosResponse<unknown, MasterDataResponse> = result as AxiosResponse;
            if (response && response.status === 200 && response.data) {
                let data = response.data as MasterDataItem[];
                let newMasterData: MasterData = {
                    counselorIdOptions: []
                };

                let counselorIdOptions: PageSizeOption[] = data.map((item, index) => {
                    return {
                        key: index,
                        value: item.id,
                        text: item.value,
                    }
                });
                counselorIdOptions.unshift({ key: -1, value: 0, text: 'Any' });

                newMasterData.counselorIdOptions = counselorIdOptions;
                setMasterData(newMasterData);

            }
            callBack();
        }, (error) => {
            setIsLoading(false);
            console.log(error);
        });
    }

    const _saveState = (currentPayload: GetESPPLPayload) => {
        let stateName = statePropName || AppConfig.RSA_SPECIALLIST_MEETING_REQUEST_LIST_STATE;
        if (saveState) {
            utils.updateInLocalStorage(stateName, JSON.stringify(currentPayload));
        }
    }

    const _loadState = () => {
        let stateName = statePropName || AppConfig.RSA_SPECIALLIST_MEETING_REQUEST_LIST_STATE;
        let stateString: string | null = utils.getLocalStorageItem(stateName);
        if (stateString && saveState) {
            return JSON.parse(stateString) as GetESPPLPayload;
        } else {
            return null;
        }

    }

    useEffect(() => {
        _fetchMasterData(() => {
            let priviousState: GetESPPLPayload | null = _loadState();
            if (priviousState && saveState) {
                _fetchData(priviousState);
                setSortByField(priviousState.sortByField);
                setSortOrderDesc(priviousState.sortOrderDesc);
                setSearchText(priviousState.searchText);
                setRecordsPerPage(priviousState.recordsPerPage);
                setCounselorId(priviousState.counselorId);
                setPageNum(priviousState.pageNum);
                if (searchRef.current) {
                    searchRef.current.value = priviousState.searchText || '';
                }

            } else {
                _fetchData();
            }
        });

    }, []);

    // column definition

    let columns: ColumnDefinition[];

    columns = [
        {
            columnName: 'select',
            sortable: false,
            columnHeader: 'Select',
            decorator: (row: GetESPPLDataRow, data: GetESPPLDataRow[], rowIndex: number, colIndex: number) => {
                return (<span><Checkbox
                    label=''
                    onChange={(event, {checked})=>{ 
                        let newValue = checked || false;
                        handleSelectBoxClick(newValue, row.employerSpotlightPresentationParticipantId || '');}}
                /></span>);
            },
        },
        {
            columnName: 'name',
            sortable: false,
            columnHeader: 'Name',
            decorator: undefined,
        },
        {
            columnName: 'counselor',
            sortable: false,
            columnHeader: 'Counselor',
            decorator: undefined,
        },
        {
            columnName: 'email',
            sortable: false,
            columnHeader: 'Email address',
            decorator: undefined,
        },
        {
            columnName: 'phone',
            sortable: false,
            columnHeader: 'Phone number',
            decorator: (row: GetESPPLDataRow, data: GetESPPLDataRow[], rowIndex: number, colIndex: number) => {
                return (<span>{utils.formatPhoneNumber(row.phone)}</span>);
            },
        },
    ];

    return [columns, recordsPerPage, totalRecords, pageNum,
        pageSizeOptions, searchRef, searchText, data, isLoading, sortOrderDesc, sortByField,
        counselorId, handleCounselorIdChange, masterData, presentationData,
        handlePageSizeChange, handleSearchChange, resetSearch, handlePaginationChange, handleSortChange,exportToExcel,
    ] as const;
}